import React, { Component } from 'react';
import { Card, Badge, Button, Collapse, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faTimesCircle,
  faCode,
  faExclamationTriangle,
  faTable
} from '@fortawesome/free-solid-svg-icons';

import { AGDataGrid } from '../common/AGDataGrid';
import Sidebar from '../common/Sidebar'; // Import your reusable sidebar
import {toTitleCase} from '../constants';

interface DVProps {
  data: any;
  recordStore: Record<string, any>;
}

interface DVState {
  expandedRules: Record<string, boolean>;
  activeType: string;
  selectedSamples: any[];
  collapsed: boolean;
}

export default class DataValidationResults extends Component<DVProps, DVState> {
  state: DVState = {
    expandedRules: {},
    activeType: '',
    selectedSamples: [],
    collapsed: true
  };

  getGroupedRules = () => {
    const groups: Record<string, any[]> = {};
    for (const rule of this.props.data.validation_rules) {
      if (!groups[rule.type]) groups[rule.type] = [];
      groups[rule.type].push(rule);
    }
    return groups;
  };

  getIconForType = (type: string) => {
    if (type.includes('data_type')) return faCode;
    if (type.includes('missing')) return faExclamationTriangle;
    return faTable;
  };

  toggleExpand = (ruleKey: string) => {
    this.setState(prev => ({
      expandedRules: {
        ...prev.expandedRules,
        [ruleKey]: !prev.expandedRules[ruleKey],
      },
    }));
  };

  showFailedRecords = (rowIds: string[]) => {
    const samples = rowIds.map(id => this.props.recordStore[id]).filter(Boolean);
    this.setState({ selectedSamples: samples });
  };

  getSampleGridColumns = () => {
    const sampleRecord = this.state.selectedSamples?.[0];
    const profiledCol = this.props.data?.column;

    if (!sampleRecord) return [];

    const allFields = Object.keys(sampleRecord);
    const reorderedFields = [profiledCol, ...allFields.filter(key => key !== profiledCol)];

    return reorderedFields.map(key => ({
      field: key,
      headerName: key,
      cellClass: key === profiledCol ? 'highlighted-column' : '',
    }));
  };

  render() {
    const grouped = this.getGroupedRules();
    const { expandedRules, selectedSamples, activeType, collapsed } = this.state;
    const activeKey = activeType || Object.keys(grouped)[0];

    const sidebarItems = Object.keys(grouped).map(type => ({
      key: type,
      label: toTitleCase(type.replace(/_/g, ' ')),
      icon: this.getIconForType(type)
    }));

    return (
      <div className="d-flex" style={{ minHeight: '100vh' }}>
        {/* Sub-Sidebar for Validation Types */}
        <Sidebar
          items={sidebarItems}
          activeKey={activeKey}
          onSelect={(key) => this.setState({ activeType: key, selectedSamples: [] })}
          collapsed={collapsed}
          onToggleCollapse={() => this.setState({ collapsed: !collapsed })}
           theme="grayBordered"   
          width={180}
          fontSize="13px"
          compact={false}
        />

        {/* Main Content */}
        <div className="flex-grow-1 p-4">
          {grouped[activeKey]?.map((rule, idx) => {
            const ruleKey = `${activeKey}-${idx}`;
            const failed = rule.result.status === 'Failed';

            return (
              <Card key={ruleKey} className="mb-3 shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{rule.description}</strong>
                      <br />
                      <small className="text-muted">{rule.example_logic}</small>
                    </div>
                    <Badge bg={failed ? 'danger' : 'success'}>
                      {rule.result.status}
                    </Badge>
                  </div>

                  {failed && rule.result.failed_count > 0 && (
                    <div className="mt-3">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => this.toggleExpand(ruleKey)}
                      >
                        {expandedRules[ruleKey] ? 'Hide' : 'View'} Failed Records ({rule.result.failed_count})
                      </Button>
                      <Collapse in={expandedRules[ruleKey]}>
                        <div className="mt-2">
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => this.showFailedRecords(rule.result.failed_row_refs)}
                          >
                            Show Sample Records
                          </Button>
                        </div>
                      </Collapse>
                    </div>
                  )}
                </Card.Body>
              </Card>
            );
          })}

          {/* AG Grid Section */}
          {selectedSamples.length > 0 && (
            <div className="mt-3 p-3 border-top bg-white" style={{ height: '50vh' }}>
              <AGDataGrid
                title="Failed Sample Records"
                columnDefs={this.getSampleGridColumns()}
                rowData={selectedSamples}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
}
